import { CLASSES, CLASS_ORDER } from '../shared/classes';
import { abilitiesFor } from '../shared/abilities';
import type { AbilityDef } from '../shared/abilities';
import { CHAT_COOLDOWN_MS, MAX_INVENTORY } from '../shared/constants';
import {
  activeSets,
  canClassEquip,
  describeBonus,
  EQUIP_SLOTS,
  gearPrice,
  gearSet,
  gearValue,
  nextRarity,
  RARITIES,
  scaleBonus,
  setCount,
  upgradeCost,
} from '../shared/equipment';
import type { EquipSlot, GearItem, StatBonus } from '../shared/equipment';
import { ITEMS, itemPrice, SHOP_ORDER } from '../shared/items';
import { MAX_ACTIVE_QUESTS, offersFor, questBlurb } from '../shared/quests';
import { deriveStats } from '../shared/progression';
import type { ClassId, ItemId, NpcInfo, NpcRole, PointOfInterest } from '../shared/types';
import type { ChatLine, PlayerSnapshot, QuestSnapshot, SocialState, StatKey } from '../shared/protocol';
import { drawAbilityCanvas, drawIconCanvas } from './icons';
import { drawPlayerBody } from './sprites';
import { CHANGELOG } from './changelog';

function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`UI element #${id} is missing from index.html`);
  return node as T;
}

const ATTR_INFO: Record<StatKey, { label: string; desc: string }> = {
  str: { label: 'STR', desc: '+2.5 attack' },
  agi: { label: 'AGI', desc: '+4 speed, faster swings' },
  vit: { label: 'VIT', desc: '+8 max health' },
};

const ATTR_ORDER: StatKey[] = ['str', 'agi', 'vit'];

/** Mirrors the server's cap on a feedback message. */
const FEEDBACK_MAX = 500;

const SLOT_LABELS: Record<EquipSlot, string> = {
  weapon: 'Weapon',
  head: 'Head',
  body: 'Body',
  feet: 'Feet',
  trinket: 'Trinket',
};

interface RosterEntry {
  name: string;
  classId: ClassId;
  level: number;
  coins: number;
  lastSeen: number;
}

/** Stats shown in the comparison tooltip, in reading order. */
const TOOLTIP_STATS: Array<{ key: keyof StatBonus; label: string; percent?: boolean }> = [
  { key: 'attack', label: 'Attack' },
  { key: 'defense', label: 'Defence' },
  { key: 'maxHp', label: 'Max Health' },
  { key: 'maxMp', label: 'Max Mana' },
  { key: 'speed', label: 'Move Speed' },
  { key: 'crit', label: 'Crit Chance', percent: true },
  { key: 'str', label: 'STR' },
  { key: 'agi', label: 'AGI' },
  { key: 'vit', label: 'VIT' },
];

function fmtStat(value: number, percent?: boolean): string {
  return percent ? `${(value * 100).toFixed(1)}%` : String(Math.round(value));
}

function relativeTime(timestamp: number): string {
  const seconds = Math.max(0, (Date.now() - timestamp) / 1000);
  if (seconds < 90) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * The character sheet is a fixed list of rows. It is built once and only the
 * values are written to afterwards - rebuilding these nodes every frame would
 * destroy the attribute buttons between a mousedown and its matching mouseup,
 * so the click would never fire.
 */
const STAT_ROWS: Array<[label: string, key: string]> = [
  ['Class', 'class'],
  ['Level', 'level'],
  ['Next level', 'nextLevel'],
  ['Max health', 'maxHealth'],
  ['Max mana', 'maxMana'],
  ['Attack', 'attack'],
  ['Defence', 'defence'],
  ['Move speed', 'moveSpeed'],
  ['Attacks / sec', 'attackSpeed'],
  ['Reach', 'reach'],
  ['Mana regen', 'manaRegen'],
  ['Coins', 'coins'],
  ['Kills', 'kills'],
  ['Deaths', 'deaths'],
];

export interface UiHandlers {
  onPlay: (name: string, classId: ClassId) => void;
  onSpend: (stat: StatKey) => void;
  onChatSend: (text: string) => void;
  onChatOpenChange: (open: boolean) => void;
  onBuy: (item: ItemId, qty: number) => void;
  onUse: (item: ItemId) => void;
  onEquip: (uid: string) => void;
  onUnequip: (slot: EquipSlot) => void;
  onSell: (uid: string) => void;
  onBuyGear: (uid: string) => void;
  onUpgrade: (uid: string) => void;
  onAbility: (slot: 0 | 1) => void;
  onAcceptQuest: (id: string) => void;
  onAbandonQuest: (id: string) => void;
  onPartyInvite: (name: string) => void;
  onPartyAccept: (name: string) => void;
  onPartyDecline: (name: string) => void;
  onPartyLeave: () => void;
  onPartyKick: (name: string) => void;
  onFriendAdd: (name: string) => void;
  onFriendRemove: (name: string) => void;
  /** The pause menu. */
  onResume: () => void;
  onLogout: () => void;
}

interface AbilitySlotView {
  root: HTMLElement;
  canvas: HTMLCanvasElement;
  cooldown: HTMLElement;
  cooldownText: HTMLElement;
  def: AbilityDef;
}

export class UI {
  private handlers: UiHandlers;
  selectedClass: ClassId = 'warrior';
  chatOpen = false;

  private previewCanvases = new Map<ClassId, HTMLCanvasElement>();
  private chatLog = el('chat-log');
  private chatInput = el<HTMLInputElement>('chat-input');
  private nameInput = el<HTMLInputElement>('name-input');
  private loginError = el('login-error');
  private loginScreen = el('login');
  private hud = el('hud');
  private deathScreen = el('death');
  private connecting = el('connecting');
  private connectingText = el('connecting-text');
  private confirmPanel = el('confirm');
  private confirmTitle = el('confirm-title');
  private confirmText = el('confirm-text');
  private confirmOk = el<HTMLButtonElement>('confirm-ok');
  private confirmCancel = el<HTMLButtonElement>('confirm-cancel');
  private confirmHandler: (() => void) | null = null;
  private feedbackForm = el<HTMLFormElement>('feedback-form');
  private feedbackInput = el<HTMLTextAreaElement>('feedback-input');
  private feedbackCount = el('feedback-count');
  private feedbackStatus = el('feedback-status');
  private feedbackSend = el<HTMLButtonElement>('feedback-send');
  private charPanel = el('char-panel');
  private statGrid = el('stat-grid');
  private attrList = el('attr-list');
  private pointBanner = el('point-banner');
  private pointCount = el('point-count');
  private gearPanel = el('gear-panel');
  private gearSlots = el('gear-slots');
  private gearSets = el('gear-sets');
  private gearList = el('gear-list');
  private gearCount = el('gear-count');
  private toastHost = el('toast');
  private levelBanner = el('levelup-banner');
  private tradePrompt = el('trade-prompt');
  private itemTooltip = el('item-tooltip');
  private shopPanel = el('shop-panel');
  private shopList = el('shop-list');
  private shopGear = el('shop-gear');
  private shopCoins = el('shop-coins');
  private shopName = el('shop-name');
  private coinsEl = el('coins');
  private bars = {
    hpFill: el('hp-fill'),
    mpFill: el('mp-fill'),
    xpFill: el('xp-fill'),
    hpText: el('hp-text'),
    mpText: el('mp-text'),
    xpText: el('xp-text'),
  };

  private lastSnapshot: PlayerSnapshot | null = null;
  /** Local mirror of the server's chat cooldown, for instant feedback. */
  private lastChatAt = 0;

  private roster: RosterEntry[] = [];
  private creatorVisible = false;
  private rosterPanel = el('roster');
  private rosterList = el('roster-list');
  private creatorPanel = el('creator');
  private backToRoster = el('back-to-roster');

  // Character sheet, built lazily and then only patched.
  private charBuilt = false;
  private statValues = new Map<string, HTMLElement>();
  private attrValues = new Map<StatKey, HTMLElement>();
  private attrButtons = new Map<StatKey, HTMLButtonElement>();

  // Shop, built lazily and then only patched.
  private shopBuilt = false;
  private shopBuyButtons = new Map<string, HTMLButtonElement>();

  private quickSlots = new Map<ItemId, HTMLElement>();
  private quickCounts = new Map<ItemId, HTMLElement>();
  private quickCache = new Map<ItemId, number>();
  private abilitySlots: AbilitySlotView[] = [];
  /** Last rendered cooldown per slot, so unchanged frames touch no DOM. */
  private abilityCache: Array<{ height: number; text: string; noMana: boolean }> = [];
  private abilityBar = el('abilitybar');

  private questPanel = el('quest-panel');
  private questOffers = el('quest-offers');
  private questActive = el('quest-active');
  private questCapacity = el('quest-capacity');
  private questGiverName = el('quest-giver-name');
  private questTracker = el('quest-tracker');
  private trackerList = el('quest-tracker-list');
  /** Signature of the current offer list, so rows are only rebuilt on change. */
  private offersKey = '';
  /** Landmarks, so boss bounties can name and target them. */
  private landmarks: PointOfInterest[] = [];
  private offerButtons = new Map<string, HTMLButtonElement>();
  private activeQuestRows = new Map<
    string,
    { root: HTMLElement; label: HTMLElement; bar: HTMLElement; button: HTMLButtonElement }
  >();
  private trackerKey = '';
  /** Signature of the current loadout, so the gear panel only rebuilds on change. */
  private gearKey = '';
  /** Latest gear state pushed by the server. */
  private equipment: Partial<Record<EquipSlot, GearItem>> = {};
  private inventory: GearItem[] = [];
  private shopWeapons: GearItem[] = [];
  private shopArmour: GearItem[] = [];
  /** Which vendor's panel is currently open. */
  private shopRole: NpcRole | null = null;
  /** Signature of the rendered shop stock, so it only rebuilds on change. */
  private shopGearKey = '';

  private socialPanel = el('social-panel');  private socialInvites = el('social-invites');
  private partyList = el('party-list');
  private partyCount = el('party-count');
  private partyLeave = el('party-leave');
  private friendList = el('friend-list');
  private friendCount = el('friend-count');
  private friendForm = el<HTMLFormElement>('friend-form');
  private friendInput = el<HTMLInputElement>('friend-input');
  private onlineList = el('online-list');
  private onlineCount = el('online-count');
  /** Latest social state, kept so the panel can be rebuilt when opened. */
  private social: SocialState | null = null;

  private pauseMenu = el('pause-menu');
  private perfPanel = el('perf');

  constructor(handlers: UiHandlers) {
    this.handlers = handlers;
    this.buildClassCards();
    this.buildQuickbar();
    this.renderChangelog();
    this.bindEvents();
    // Paint the class previews immediately; the game loop animates them later.
    this.drawPreviews(0);
  }

  /* ------------------------------------------------------------------- login */

  /**
   * Asks the server which characters exist and shows them as one-click cards.
   * Falls back to the plain creator if the request fails.
   */
  async loadRoster(): Promise<void> {
    try {
      const response = await fetch('/api/characters', { cache: 'no-store' });
      const data = (await response.json()) as { characters?: RosterEntry[]; persistent?: boolean };
      this.roster = Array.isArray(data.characters) ? data.characters : [];
      // Warn on the login screen if this server is not saving to a volume.
      el('storage-warning').classList.toggle('hidden', data.persistent !== false);
    } catch {
      this.roster = [];
    }
    this.renderRoster();
  }

  /** Landmarks arrive with the world, once, so boss bounties can target them. */
  setLandmarks(landmarks: PointOfInterest[]): void {
    this.landmarks = landmarks;
    this.offersKey = '\u0000';
  }

  get confirmOpen(): boolean {
    return !this.confirmPanel.classList.contains('hidden');
  }

  /** Shows a yes/no dialog, used to make destructive actions deliberate. */
  private confirmAction(title: string, text: string, okLabel: string, onOk: () => void): void {
    this.confirmTitle.textContent = title;
    this.confirmText.textContent = text;
    this.confirmOk.textContent = okLabel;
    this.confirmHandler = onOk;
    this.confirmPanel.classList.remove('hidden');
  }

  private closeConfirm(): void {
    this.confirmPanel.classList.add('hidden');
    this.confirmHandler = null;
  }

  private async deleteCharacter(name: string): Promise<void> {
    try {
      const response = await fetch('/api/characters/delete', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) {
        this.setError(data.error ?? 'Could not delete that character.');
        return;
      }
      await this.loadRoster();
    } catch {
      this.setError('Could not reach the server.');
    }
  }

  private async sendFeedback(): Promise<void> {
    const message = this.feedbackInput.value.trim();
    if (message.length === 0) return;
    this.feedbackSend.disabled = true;
    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ message, name: this.nameInput.value.trim() }),
      });
      if (!response.ok) throw new Error('failed');
      this.feedbackInput.value = '';
      this.feedbackCount.textContent = `0 / ${FEEDBACK_MAX}`;
      this.feedbackStatus.textContent = 'Thank you - your feedback has been sent.';
      this.feedbackStatus.className = 'feedback-status';
    } catch {
      this.feedbackStatus.textContent = 'Could not send that just now. Try again in a moment.';
      this.feedbackStatus.className = 'feedback-status error';
    } finally {
      this.feedbackSend.disabled = false;
    }
  }

  /** Fills the release-notes panel from the changelog data. */
  private renderChangelog(): void {
    const list = el('changelog-list');
    list.innerHTML = '';

    for (const release of CHANGELOG) {
      const section = document.createElement('div');
      section.className = 'release';

      const head = document.createElement('div');
      head.className = 'release-head';
      const version = document.createElement('b');
      version.className = 'release-version';
      version.textContent = `v${release.version}`;
      const title = document.createElement('span');
      title.className = 'release-title';
      title.textContent = release.title;
      const date = document.createElement('span');
      date.className = 'release-date';
      date.textContent = release.date;
      head.append(version, title, date);

      const changes = document.createElement('ul');
      for (const change of release.changes) {
        const item = document.createElement('li');
        item.textContent = change;
        changes.appendChild(item);
      }

      section.append(head, changes);
      list.appendChild(section);
    }

    // The summary doubles as a headline for the newest release.
    const latest = CHANGELOG[0];
    el('changelog-summary').textContent = latest
      ? `What's new - v${latest.version}: ${latest.title}`
      : "What's new";
  }

  private renderRoster(): void {
    this.rosterList.innerHTML = '';

    for (const entry of this.roster) {
      const def = CLASSES[entry.classId] ?? CLASSES.warrior;
      // A div rather than a button, so the delete control can nest inside it.
      const card = document.createElement('div');
      card.className = 'roster-card';
      card.setAttribute('role', 'button');
      card.tabIndex = 0;

      const portrait = document.createElement('span');
      portrait.className = 'roster-portrait';
      portrait.style.background = def.color;
      portrait.style.color = def.accent;
      portrait.textContent = entry.name.charAt(0).toUpperCase();

      const info = document.createElement('span');
      info.className = 'roster-info';
      const name = document.createElement('span');
      name.className = 'roster-name';
      name.textContent = entry.name;
      const meta = document.createElement('span');
      meta.className = 'roster-meta';
      const level = document.createElement('b');
      level.textContent = `Level ${entry.level}`;
      meta.append(level, document.createTextNode(` ${def.name} \u00b7 ${entry.coins} coins`));
      info.append(name, meta);

      const when = document.createElement('span');
      when.className = 'roster-when';
      when.textContent = relativeTime(entry.lastSeen);

      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'roster-delete';
      del.title = `Delete ${entry.name}`;
      del.setAttribute('aria-label', `Delete ${entry.name}`);
      del.textContent = '\u00d7';
      del.addEventListener('click', (event) => {
        event.stopPropagation();
        this.confirmAction(
          'Delete character?',
          `Delete ${entry.name}? This cannot be undone.`,
          'Delete',
          () => void this.deleteCharacter(entry.name),
        );
      });

      card.append(portrait, info, when, del);
      // One click and you are in - the character's own class is used, whatever
      // is currently selected in the creator below.
      const activate = (): void => {
        this.nameInput.value = entry.name;
        this.clearError();
        this.setPlayEnabled(false);
        this.showConnecting(`Returning as ${entry.name}...`);
        this.handlers.onPlay(entry.name, entry.classId);
      };
      card.addEventListener('click', activate);
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate();
        }
      });
      this.rosterList.appendChild(card);
    }

    // Skip the roster entirely on a fresh server.
    if (this.roster.length === 0) this.creatorVisible = true;
    this.updateLoginMode();
  }

  private updateLoginMode(): void {
    const hasRoster = this.roster.length > 0;
    const showRoster = hasRoster && !this.creatorVisible;
    this.rosterPanel.classList.toggle('hidden', !showRoster);
    this.creatorPanel.classList.toggle('hidden', showRoster);
    this.backToRoster.classList.toggle('hidden', !hasRoster);
  }

  private buildClassCards(): void {
    const host = el('class-list');
    host.innerHTML = '';
    for (const id of CLASS_ORDER) {
      const def = CLASSES[id];
      const base = deriveStats(id, 1, { str: 0, agi: 0, vit: 0 });

      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'class-card';
      card.dataset.class = id;
      card.style.setProperty('--class-color', def.color);

      const canvas = document.createElement('canvas');
      canvas.className = 'glyph';
      canvas.width = 72;
      canvas.height = 92;
      card.appendChild(canvas);
      this.previewCanvases.set(id, canvas);

      const h3 = document.createElement('h3');
      h3.textContent = def.name;
      card.appendChild(h3);

      const tag = document.createElement('div');
      tag.className = 'tagline-small';
      tag.textContent = def.tagline;
      card.appendChild(tag);

      const description = document.createElement('p');
      description.textContent = def.description;
      card.appendChild(description);

      const list = document.createElement('dl');
      const rows: Array<[string, string]> = [
        ['Health', String(base.maxHp)],
        ['Mana', String(base.maxMp)],
        ['Attack', base.attack.toFixed(1)],
        ['Defence', base.defense.toFixed(1)],
        ['Speed', String(base.speed)],
        ['Range', def.attack.kind === 'melee' ? 'Melee' : `${base.range}`],
      ];
      for (const [key, value] of rows) {
        const dt = document.createElement('dt');
        dt.textContent = key;
        const dd = document.createElement('dd');
        dd.textContent = value;
        list.append(dt, dd);
      }
      card.appendChild(list);

      card.addEventListener('click', () => this.selectClass(id));
      host.appendChild(card);
    }
    this.selectClass(this.selectedClass);
  }

  selectClass(id: ClassId): void {
    this.selectedClass = id;
    for (const card of document.querySelectorAll<HTMLElement>('.class-card')) {
      card.classList.toggle('selected', card.dataset.class === id);
    }
  }

  /** Idle animation for the class previews on the login screen. */
  drawPreviews(t: number): void {
    for (const [id, canvas] of this.previewCanvases) {
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const width = canvas.width / dpr;
      const height = canvas.height / dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.save();
      // Stand the character on the floor of the tile, facing the viewer.
      ctx.translate(width / 2, height - 10);
      const offset = id === 'mage' ? 1 : id === 'ranger' ? 2 : 0;
      drawPlayerBody(ctx, id, -Math.PI / 2.2, t + offset, 15, {
        swing: 0,
        moving: 0,
        flash: 0,
        alpha: 1,
      });
      ctx.restore();
    }
  }

  setError(message: string): void {
    this.loginError.textContent = message;
    this.loginError.classList.remove('hidden');
    this.setPlayEnabled(true);
  }

  clearError(): void {
    this.loginError.classList.add('hidden');
  }

  setPlayEnabled(enabled: boolean): void {
    el<HTMLButtonElement>('play-btn').disabled = !enabled;
  }

  showConnecting(text: string): void {
    this.connectingText.textContent = text;
    this.connecting.classList.remove('hidden');
  }

  hideConnecting(): void {
    this.connecting.classList.add('hidden');
  }

  showLogin(): void {
    this.loginScreen.classList.remove('hidden');
    this.hud.classList.add('hidden');
    this.hideConnecting();
    this.closePause();
    this.closeShop();
    this.closeQuestPanel();
    this.closeSocialPanel();
    this.social = null;
    this.setPlayEnabled(true);
    // Make sure whatever tab the player was on is the one they come back to.
    this.updateLoginMode();
  }

  hideLogin(): void {
    this.loginScreen.classList.add('hidden');
    this.hud.classList.remove('hidden');
    this.hideConnecting();
  }

  /* -------------------------------------------------------------------- chat */

  pushChat(line: ChatLine): void {
    const p = document.createElement('p');
    if (line.system) {
      p.className = 'sys';
      p.textContent = line.text;
    } else {
      if (line.channel === 'party') p.classList.add('party');

      const time = document.createElement('span');
      time.className = 'time';
      time.textContent = new Date(line.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      const from = document.createElement('span');
      from.className = 'from';
      from.textContent = `${line.channel === 'party' ? '[Party] ' : ''}${line.from}: `;
      from.style.color =
        line.channel === 'party' ? '#8ee08a' : line.cls ? CLASSES[line.cls].accent : '#c9d4e6';

      const body = document.createElement('span');
      body.textContent = line.text;

      p.append(time, from, body);
    }
    this.chatLog.appendChild(p);
    while (this.chatLog.childElementCount > 80) this.chatLog.removeChild(this.chatLog.firstChild!);
    this.chatLog.scrollTop = this.chatLog.scrollHeight;
  }

  clearChat(): void {
    this.chatLog.innerHTML = '';
  }

  openChat(): void {
    if (this.chatOpen) return;
    this.chatOpen = true;
    this.chatInput.focus();
    this.handlers.onChatOpenChange(true);
  }

  closeChat(): void {
    if (!this.chatOpen) return;
    this.chatOpen = false;
    this.chatInput.value = '';
    this.chatInput.blur();
    this.handlers.onChatOpenChange(false);
  }

  toggleChat(): void {
    if (this.chatOpen) this.closeChat();
    else this.openChat();
  }

  /**
   * Sends whatever is in the box, then empties it and keeps the chat open so
   * the next message can be typed straight away. Pressing Enter on an empty
   * box leaves the chat instead.
   */
  submitChat(): void {
    const text = this.chatInput.value.trim();
    if (!text) {
      this.closeChat();
      return;
    }

    const now = Date.now();
    if (now - this.lastChatAt < CHAT_COOLDOWN_MS) {
      const wait = ((CHAT_COOLDOWN_MS - (now - this.lastChatAt)) / 1000).toFixed(1);
      this.addToast(`Slow down - try again in ${wait}s.`, 'warn');
      return;
    }

    this.lastChatAt = now;
    this.handlers.onChatSend(text);
    // Erase what was sent, but stay open and focused for the next line.
    this.chatInput.value = '';
  }

  /* --------------------------------------------------------------------- HUD */

  /**
   * The HUD is refreshed every frame, so element lookups are cached and writes
   * are skipped when the value has not changed. On Firefox the DOM churn from
   * ~25 writes plus several getElementById calls per frame is measurable.
   */
  private hudRefs: {
    pname: HTMLElement;
    plevel: HTMLElement;
    portrait: HTMLElement;
    glyph: HTMLElement;
  } | null = null;
  private hudCache = {
    name: '',
    level: '',
    coins: '',
    cls: '',
    hpWidth: -1,
    mpWidth: -1,
    xpWidth: -1,
    hpText: '',
    mpText: '',
    xpText: '',
  };

  updateHud(snap: PlayerSnapshot): void {
    this.lastSnapshot = snap;
    this.hudRefs ??= {
      pname: el('pname'),
      plevel: el('plevel'),
      portrait: el('portrait'),
      glyph: el('portrait-glyph'),
    };

    const setBar = (fill: HTMLElement, key: 'hpWidth' | 'mpWidth' | 'xpWidth', pct: number): void => {
      const value = Math.round(Math.max(0, Math.min(100, pct)) * 10) / 10;
      if (this.hudCache[key] === value) return;
      this.hudCache[key] = value;
      fill.style.width = `${value}%`;
    };
    const setText = (node: HTMLElement, key: keyof typeof this.hudCache, value: string): void => {
      if (this.hudCache[key] === value) return;
      (this.hudCache as Record<string, string | number>)[key] = value;
      node.textContent = value;
    };

    setBar(this.bars.hpFill, 'hpWidth', (snap.hp / snap.maxHp) * 100);
    setBar(this.bars.mpFill, 'mpWidth', (snap.mp / snap.maxMp) * 100);
    setBar(this.bars.xpFill, 'xpWidth', (snap.xp / Math.max(1, snap.xpNext)) * 100);

    setText(this.bars.hpText, 'hpText', `${Math.max(0, Math.ceil(snap.hp))} / ${snap.maxHp}`);
    setText(this.bars.mpText, 'mpText', `${Math.floor(snap.mp)} / ${snap.maxMp}`);
    setText(this.bars.xpText, 'xpText', `${snap.xp} / ${snap.xpNext}`);

    setText(this.coinsEl, 'coins', String(snap.coins));
    setText(this.hudRefs.pname, 'name', snap.name);
    setText(this.hudRefs.plevel, 'level', `Lv ${snap.level}`);
    setText(this.hudRefs.glyph, 'cls', CLASSES[snap.cls].name[0]);

    if (this.hudCache.cls !== snap.cls) {
      this.hudCache.cls = snap.cls;
      const def = CLASSES[snap.cls];
      this.hudRefs.portrait.style.background = def.color;
      this.hudRefs.portrait.style.color = def.accent;
    }

    this.updateQuickbar(snap.bag);
    this.updateAbilities(snap);
    this.updateQuests(snap);
    if (this.charPanelOpen) this.updateCharPanel(snap);
    if (this.gearPanelOpen) this.renderGear();
    if (this.shopOpen) this.updateShop(snap);
  }

  setOnline(count: number): void {
    el('online').textContent = `${count} online`;
  }

  setPing(ms: number): void {
    el('ping').textContent = `${ms} ms`;
  }

  /* -------------------------------------------------------------------- perf */

  /** On by default, since the point of it is diagnosing a slow machine. */
  private perfEnabled = true;

  togglePerf(): void {
    this.perfEnabled = !this.perfEnabled;
    this.perfPanel.classList.toggle('hidden', !this.perfEnabled);
  }

  /**
   * Frame rate plus the numbers that actually explain a low one. The entity
   * counts matter more than the FPS figure when someone reports stutter, since
   * a crowd of monsters is usually the cause.
   */
  setPerf(stats: {
    fps: number;
    frameMs: number;
    worstMs: number;
    enemies: number;
    players: number;
    projectiles: number;
    drops: number;
    obstacles: number;
    zoom: number;
    /** Canvas backing store, the thing that actually drives fill cost. */
    canvasWidth: number;
    canvasHeight: number;
    dpr: number;
    /** Milliseconds in the canvas draw calls, versus the whole frame step. */
    drawMs: number;
    stepMs: number;
    lowQuality: boolean;
    /** Adaptive render scale, 1 = full resolution. */
    scale: number;
  }): void {
    if (!this.perfEnabled) return;
    this.perfPanel.classList.remove('hidden');

    const fpsTone = stats.fps >= 50 ? 'good' : stats.fps >= 30 ? 'warn' : 'bad';
    const worstTone = stats.worstMs <= 25 ? 'good' : stats.worstMs <= 50 ? 'warn' : 'bad';
    const megapixels = (stats.canvasWidth * stats.canvasHeight) / 1_000_000;
    const pixelTone = megapixels <= 2.2 ? 'good' : megapixels <= 3.6 ? 'warn' : 'bad';

    const row = (text: string, highlight?: string, tone?: string): HTMLDivElement => {
      const div = document.createElement('div');
      div.append(document.createTextNode(text));
      if (highlight !== undefined) {
        const span = document.createElement('span');
        if (tone) span.className = tone;
        span.textContent = highlight;
        div.appendChild(span);
      }
      return div;
    };

    const total = stats.enemies + stats.players + stats.projectiles + stats.drops;
    const idleMs = Math.max(0, stats.frameMs - stats.stepMs);
    // Where the frame actually goes: our canvas work, the rest of our logic, or
    // waiting on the browser. If idle dominates, the bottleneck is not us.
    const dominant =
      stats.drawMs >= stats.stepMs - stats.drawMs && stats.drawMs >= idleMs
        ? 'canvas'
        : stats.stepMs - stats.drawMs >= idleMs
          ? 'logic'
          : 'browser';

    this.perfPanel.replaceChildren(
      row('FPS ', String(Math.round(stats.fps)), fpsTone),
      row(
        `  ${stats.frameMs.toFixed(1)}ms average    worst `,
        `${stats.worstMs.toFixed(0)}ms`,
        worstTone,
      ),
      row(
        `  canvas `,
        `${stats.drawMs.toFixed(1)}ms`,
        dominant === 'canvas' ? fpsTone : undefined,
      ),
      row(`  logic `, `${(stats.stepMs - stats.drawMs).toFixed(1)}ms`),
      row(
        `  waiting on browser `,
        `${idleMs.toFixed(1)}ms`,
        dominant === 'browser' ? 'warn' : undefined,
      ),
      row(`entities ${total}    mob ${stats.enemies}  players ${stats.players}`),
      row(`shots ${stats.projectiles}  loot ${stats.drops}    zoom x${stats.zoom.toFixed(2)}`),
      row(`scenery ${stats.obstacles}`),
      row(
        `render ${stats.canvasWidth}x${stats.canvasHeight} x${stats.dpr.toFixed(2)}  `,
        `${megapixels.toFixed(1)}MP`,
        pixelTone,
      ),
      row(
        `quality `,
        `${stats.lowQuality ? 'low' : 'full'}${stats.scale < 0.999 ? ` · ${Math.round(stats.scale * 100)}%` : ''} (G)`,
      ),
    );
  }

  /**
   * Tells the player where they are and how tough it is: the biome they are
   * standing in, plus the level of the ground. Without this both the regions
   * and the distance-based level ramp are invisible until something eats you.
   */
  setZone(zoneLevel: number, myLevel: number, inCity: boolean, biomeName: string): void {
    const badge = el('zone-badge');
    if (inCity) {
      badge.textContent = 'Sanctuary - no monsters';
      badge.className = 'zone-badge sanctuary';
      return;
    }
    const gap = zoneLevel - myLevel;
    let tone = 'safe';
    if (gap >= 8) tone = 'deadly';
    else if (gap >= 3) tone = 'hard';
    else if (gap >= -3) tone = 'even';
    badge.textContent = `${biomeName} · Lv ${zoneLevel}`;
    badge.className = `zone-badge ${tone}`;
  }

  addToast(text: string, kind: 'info' | 'level' | 'warn' = 'info'): void {
    const div = document.createElement('div');
    div.className = `toast-item ${kind}`;
    div.textContent = text;
    this.toastHost.appendChild(div);
    window.setTimeout(() => div.remove(), 3200);
  }

  showLevelUp(level: number): void {
    el('levelup-level').textContent = String(level);
    this.levelBanner.classList.remove('hidden');
    // Restart the CSS animation.
    this.levelBanner.style.animation = 'none';
    void this.levelBanner.offsetHeight;
    this.levelBanner.style.animation = '';
    window.setTimeout(() => this.levelBanner.classList.add('hidden'), 2500);
  }

  showDeath(by: string): void {
    el('death-by').textContent = by ? `Slain by ${by}` : '';
    this.deathScreen.classList.remove('hidden');
    this.closeShop();
  }

  updateDeathTimer(seconds: number): void {
    el('respawn-timer').textContent = String(Math.max(0, Math.ceil(seconds)));
  }

  hideDeath(): void {
    this.deathScreen.classList.add('hidden');
  }

  /* --------------------------------------------------------------- ability bar */

  /** Rebuilds the two ability slots for the class the player picked. */
  setClass(classId: ClassId): void {
    const defs = abilitiesFor(classId);
    this.abilityBar.innerHTML = '';
    this.abilitySlots = [];

    defs.forEach((def, index) => {
      const root = document.createElement('div');
      root.className = 'ability-slot';
      root.title = `${def.name} (${def.key}) - ${def.description} Costs ${def.mpCost} mana.`;

      const key = document.createElement('kbd');
      key.textContent = def.key;

      const canvas = document.createElement('canvas');
      drawAbilityCanvas(canvas, def, 36);

      const cooldown = document.createElement('div');
      cooldown.className = 'ability-cd';

      const cooldownText = document.createElement('span');
      cooldownText.className = 'ability-cd-text';

      // The mana cost is always on show, so a greyed slot explains itself.
      const cost = document.createElement('span');
      cost.className = 'ability-cost';
      cost.textContent = String(def.mpCost);

      root.append(key, canvas, cooldown, cooldownText, cost);
      root.addEventListener('click', () => this.handlers.onAbility(index === 0 ? 0 : 1));
      this.abilityBar.appendChild(root);

      this.abilitySlots.push({ root, canvas, cooldown, cooldownText, def });
    });
  }

  private updateAbilities(snap: PlayerSnapshot): void {
    for (let i = 0; i < this.abilitySlots.length; i++) {
      const slot = this.abilitySlots[i];
      const remaining = snap.cds?.[i] ?? 0;
      const ratio = slot.def.cooldown > 0 ? remaining / slot.def.cooldown : 0;
      const height = Math.round(Math.max(0, Math.min(1, ratio)) * 100);
      const text = remaining > 0.05 ? String(Math.ceil(remaining)) : '';
      const noMana = snap.mp < slot.def.mpCost;

      const cache = (this.abilityCache[i] ??= { height: -1, text: '\u0000', noMana: false });
      if (cache.height !== height) {
        cache.height = height;
        slot.cooldown.style.height = `${height}%`;
      }
      if (cache.text !== text) {
        cache.text = text;
        slot.cooldownText.textContent = text;
      }
      if (cache.noMana !== noMana) {
        cache.noMana = noMana;
        slot.root.classList.toggle('no-mana', noMana);
      }
    }
  }

  /* -------------------------------------------------------------- quick bar */

  private buildQuickbar(): void {
    for (const id of SHOP_ORDER) {
      const slot = el(`slot-${id}`);
      const count = el(`count-${id}`);
      const holder = slot.querySelector<HTMLElement>('.quick-icon');
      if (holder) {
        holder.textContent = '';
        const canvas = document.createElement('canvas');
        holder.appendChild(canvas);
        drawIconCanvas(canvas, id, 26);
      }
      this.quickSlots.set(id, slot);
      this.quickCounts.set(id, count);
      slot.addEventListener('click', () => this.handlers.onUse(id));
    }
  }

  updateQuickbar(bag: Record<ItemId, number>): void {
    for (const id of SHOP_ORDER) {
      const have = bag[id] ?? 0;
      if (this.quickCache.get(id) === have) continue;
      this.quickCache.set(id, have);
      const count = this.quickCounts.get(id);
      const slot = this.quickSlots.get(id);
      if (count) count.textContent = String(have);
      slot?.classList.toggle('empty', have <= 0);
    }
  }

  /* -------------------------------------------------------------- char panel */

  get charPanelOpen(): boolean {
    return !this.charPanel.classList.contains('hidden');
  }

  toggleCharPanel(): void {
    if (this.charPanelOpen) {
      this.closeCharPanel();
      return;
    }
    this.closeShop();
    this.closeQuestPanel();
    this.closeGearPanel();
    this.ensureCharPanel();
    this.charPanel.classList.remove('hidden');
    if (this.lastSnapshot) this.updateCharPanel(this.lastSnapshot);
  }

  closeCharPanel(): void {
    this.charPanel.classList.add('hidden');
  }

  /* -------------------------------------------------------------- gear panel */

  get gearPanelOpen(): boolean {
    return !this.gearPanel.classList.contains('hidden');
  }

  toggleGearPanel(): void {
    if (this.gearPanelOpen) {
      this.closeGearPanel();
      return;
    }
    this.closeShop();
    this.closeQuestPanel();
    this.closeCharPanel();
    this.gearKey = '';
    this.gearPanel.classList.remove('hidden');
    this.renderGear();
  }

  closeGearPanel(): void {
    this.gearPanel.classList.add('hidden');
    this.hideTooltip();
  }

  /** Called when the server pushes a fresh loadout. */
  setLoadout(
    equipment: Partial<Record<EquipSlot, GearItem>>,
    inventory: GearItem[],
    shopWeapons: GearItem[],
    shopArmour: GearItem[],
  ): void {
    this.equipment = equipment;
    this.inventory = inventory;
    this.shopWeapons = shopWeapons;
    this.shopArmour = shopArmour;
    this.gearKey = '';
    if (this.gearPanelOpen) this.renderGear();
    if (this.shopOpen) this.renderShopGear();
  }

  /** Rebuilds only when the loadout actually changes, so clicks are never lost. */
  private renderGear(): void {
    const cls = this.lastSnapshot?.cls ?? 'warrior';
    const key = JSON.stringify({
      cls,
      coins: this.lastSnapshot?.coins ?? 0,
      e: EQUIP_SLOTS.map((slot) => this.equipment[slot]?.uid ?? ''),
      i: this.inventory.map((item) => item.uid),
    });
    if (key === this.gearKey) return;
    this.gearKey = key;

    this.gearSlots.innerHTML = '';
    for (const slot of EQUIP_SLOTS) {
      const item = this.equipment[slot];
      const box = document.createElement('button');
      box.type = 'button';
      box.className = `gear-slot${item ? ' filled' : ''}`;

      const label = document.createElement('span');
      label.className = 'slot-label';
      label.textContent = SLOT_LABELS[slot];
      box.appendChild(label);

      if (item) {
        box.style.setProperty('--rarity', RARITIES[item.rarity].color);
        box.addEventListener('mouseenter', () => this.showTooltip(item, undefined, box));
        box.addEventListener('mouseleave', () => this.hideTooltip());
        const name = document.createElement('span');
        name.className = 'slot-item';
        name.textContent = item.name;
        name.style.color = RARITIES[item.rarity].color;
        box.appendChild(name);
        box.addEventListener('click', () => this.handlers.onUnequip(slot));
      } else {
        const empty = document.createElement('span');
        empty.className = 'slot-item empty';
        empty.textContent = 'empty';
        box.appendChild(empty);
      }
      this.gearSlots.appendChild(box);
    }

    // Active set bonuses, so the player can see what their matching pieces add.
    this.gearSets.innerHTML = '';
    for (const active of activeSets(this.equipment)) {
      const row = document.createElement('div');
      row.className = 'gear-set-row';
      const name = document.createElement('b');
      name.textContent = `${active.set.name} (${active.count})`;
      name.style.color = active.set.color;
      const desc = document.createElement('span');
      desc.textContent = describeBonus(active.bonus).join(', ');
      row.append(name, desc);
      this.gearSets.appendChild(row);
    }

    this.gearCount.textContent = `${this.inventory.length} / ${MAX_INVENTORY}`;
    this.gearList.innerHTML = '';
    if (this.inventory.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'gear-empty';
      empty.textContent = 'Your pack is empty. Monsters drop gear - walk over it to pick it up.';
      this.gearList.appendChild(empty);
      return;
    }
    for (const item of this.inventory) this.gearList.appendChild(this.gearRow(item, cls));
  }

  private gearRow(item: GearItem, cls: ClassId): HTMLElement {
    const row = document.createElement('div');
    row.className = 'gear-row';

    const info = document.createElement('button');
    info.type = 'button';
    info.className = 'gear-item';
    const usable = canClassEquip(item.base, cls);
    info.addEventListener('mouseenter', () => this.showTooltip(item, this.equipment[item.slot] ?? null, info));
    info.addEventListener('mouseleave', () => this.hideTooltip());

    const name = document.createElement('span');
    name.className = 'gear-name';
    name.textContent = item.name;
    name.style.color = RARITIES[item.rarity].color;
    const meta = document.createElement('span');
    meta.className = 'gear-meta';
    meta.textContent = `${SLOT_LABELS[item.slot]} · ilvl ${item.ilvl}${usable ? '' : ' · unusable'}`;
    info.append(name, meta);

    if (usable) {
      info.addEventListener('click', () => this.handlers.onEquip(item.uid));
    } else {
      info.classList.add('unusable');
    }
    row.appendChild(info);

    const next = nextRarity(item.rarity);
    const cost = upgradeCost(item);
    const upgrade = document.createElement('button');
    upgrade.type = 'button';
    upgrade.className = 'gear-upgrade';
    if (next === null || cost === null) {
      upgrade.textContent = 'Max';
      upgrade.disabled = true;
      upgrade.title = 'Already the highest rarity.';
    } else {
      upgrade.textContent = `\u2191${cost.toLocaleString()}c`;
      upgrade.title = `Reforge to ${RARITIES[next].name} for ${cost} coins - weapons at Brynja, armour at Havard.`;
      upgrade.disabled = (this.lastSnapshot?.coins ?? 0) < cost;
      upgrade.addEventListener('click', () => this.handlers.onUpgrade(item.uid));
    }
    row.appendChild(upgrade);

    const sell = document.createElement('button');
    sell.type = 'button';
    sell.className = 'gear-sell';
    sell.textContent = `${gearValue(item)}c`;
    sell.title = 'Sell to the trader';
    sell.addEventListener('click', () => this.handlers.onSell(item.uid));
    row.appendChild(sell);

    return row;
  }

  /**
   * The hover tooltip. For a pack or shop item it compares side by side against
   * whatever is worn in that slot, with each stat's change coloured. Pass
   * `undefined` for `compareTo` to show a plain list (used on worn items).
   */
  private showTooltip(item: GearItem, compareTo: GearItem | null | undefined, anchor: HTMLElement): void {
    const tip = this.itemTooltip;
    tip.innerHTML = '';

    const head = document.createElement('div');
    head.className = 'tip-head';
    const name = document.createElement('span');
    name.className = 'tip-name';
    name.textContent = item.name;
    name.style.color = RARITIES[item.rarity].color;
    const sub = document.createElement('span');
    sub.className = 'tip-sub';
    sub.textContent = `${item.unique ? 'Unique · ' : ''}${RARITIES[item.rarity].name} · ${SLOT_LABELS[item.slot]} · ilvl ${item.ilvl}`;
    head.append(name, sub);
    tip.appendChild(head);

    if (compareTo === undefined) {
      const list = document.createElement('div');
      list.className = 'tip-list';
      const lines = describeBonus(item.bonuses);
      list.textContent = lines.length > 0 ? lines.join('\n') : 'No bonuses';
      tip.appendChild(list);
    } else {
      const worn = document.createElement('div');
      worn.className = 'tip-worn';
      worn.textContent = compareTo ? `Worn: ${compareTo.name}` : `Worn: nothing in ${SLOT_LABELS[item.slot].toLowerCase()}`;
      tip.appendChild(worn);

      const grid = document.createElement('div');
      grid.className = 'tip-grid';
      const cols = document.createElement('div');
      cols.className = 'tip-row tip-cols';
      for (const label of ['', 'Worn', 'This']) {
        const cell = document.createElement('span');
        cell.textContent = label;
        cols.appendChild(cell);
      }
      grid.appendChild(cols);

      for (const stat of TOOLTIP_STATS) {
        const next = item.bonuses[stat.key] ?? 0;
        const current = compareTo?.bonuses[stat.key] ?? 0;
        if (next === 0 && current === 0) continue;

        const row = document.createElement('div');
        row.className = 'tip-row';
        const label = document.createElement('span');
        label.className = 'tip-stat';
        label.textContent = stat.label;
        const cur = document.createElement('span');
        cur.className = 'tip-cur';
        cur.textContent = compareTo ? fmtStat(current, stat.percent) : '—';
        const nxt = document.createElement('span');
        nxt.className = 'tip-new';
        const delta = next - current;
        let text = fmtStat(next, stat.percent);
        if (compareTo && delta !== 0) {
          text += ` (${delta > 0 ? '+' : '-'}${fmtStat(Math.abs(delta), stat.percent)})`;
          nxt.classList.add(delta > 0 ? 'up' : 'down');
        }
        nxt.textContent = text;
        row.append(label, cur, nxt);
        grid.appendChild(row);
      }
      tip.appendChild(grid);
    }

    // Set membership, with the projected count after equipping this piece.
    const set = item.set ? gearSet(item) : undefined;
    if (set) {
      const isWorn = compareTo === undefined;
      const equipped = setCount(this.equipment, set.id);
      const wornSameSet = this.equipment[item.slot]?.set === set.id;
      const projected = isWorn ? equipped : equipped - (wornSameSet ? 1 : 0) + 1;

      const setBox = document.createElement('div');
      setBox.className = 'tip-set';
      const setTitle = document.createElement('div');
      setTitle.className = 'tip-set-name';
      setTitle.textContent = `${set.name} (${projected}/3)`;
      setTitle.style.color = set.color;
      setBox.appendChild(setTitle);

      const lines: Array<[number, StatBonus]> = [
        [2, set.bonus2],
        [3, set.bonus3],
      ];
      for (const [need, bonus] of lines) {
        const line = document.createElement('div');
        line.className = `tip-set-line${projected >= need ? ' active' : ''}`;
        line.textContent = `${need} pieces: ${describeBonus(scaleBonus(bonus, item.ilvl)).join(', ')}`;
        setBox.appendChild(line);
      }
      tip.appendChild(setBox);
    }

    tip.classList.remove('hidden');
    const rect = anchor.getBoundingClientRect();
    const width = tip.offsetWidth;
    const height = tip.offsetHeight;
    let left = rect.left - width - 10;
    if (left < 8) left = Math.min(window.innerWidth - width - 8, rect.right + 10);
    const top = Math.max(8, Math.min(rect.top, window.innerHeight - height - 8));
    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
  }

  private hideTooltip(): void {
    this.itemTooltip.classList.add('hidden');
  }

  private ensureCharPanel(): void {
    if (this.charBuilt) return;
    this.charBuilt = true;

    this.statGrid.innerHTML = '';
    this.statValues.clear();
    for (const [label, key] of STAT_ROWS) {
      const k = document.createElement('div');
      k.className = 'k';
      k.textContent = label;
      const v = document.createElement('div');
      v.className = 'v';
      this.statGrid.append(k, v);
      this.statValues.set(key, v);
    }

    this.attrList.innerHTML = '';
    this.attrValues.clear();
    this.attrButtons.clear();
    for (const stat of ATTR_ORDER) {
      const row = document.createElement('div');
      row.className = 'attr-row';

      const name = document.createElement('span');
      name.className = 'name';
      name.textContent = ATTR_INFO[stat].label;

      const desc = document.createElement('span');
      desc.className = 'desc';
      desc.textContent = ATTR_INFO[stat].desc;

      const value = document.createElement('span');
      value.className = 'val';
      value.textContent = '0';

      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = '+';
      button.title = `Spend a point on ${ATTR_INFO[stat].label}`;
      button.addEventListener('click', () => this.handlers.onSpend(stat));

      row.append(name, desc, value, button);
      this.attrList.appendChild(row);
      this.attrValues.set(stat, value);
      this.attrButtons.set(stat, button);
    }
  }

  private setStat(key: string, value: string): void {
    const node = this.statValues.get(key);
    if (node) node.textContent = value;
  }

  updateCharPanel(snap: PlayerSnapshot): void {
    this.ensureCharPanel();
    const def = CLASSES[snap.cls];
    const stats = deriveStats(snap.cls, snap.level, { str: snap.str, agi: snap.agi, vit: snap.vit });

    this.setStat('class', def.name);
    this.setStat('level', String(snap.level));
    this.setStat('nextLevel', `${Math.max(0, snap.xpNext - snap.xp)} xp`);
    this.setStat('maxHealth', String(snap.maxHp));
    this.setStat('maxMana', String(snap.maxMp));
    this.setStat('attack', stats.attack.toFixed(1));
    this.setStat('defence', stats.defense.toFixed(1));
    this.setStat('moveSpeed', String(Math.round(stats.speed)));
    this.setStat('attackSpeed', (1 / stats.cooldown).toFixed(2));
    this.setStat('reach', def.attack.kind === 'melee' ? 'Melee cone' : `${Math.round(stats.range)}`);
    this.setStat('manaRegen', `${stats.mpRegen.toFixed(1)} / sec`);
    this.setStat('coins', String(snap.coins));
    this.setStat('kills', String(snap.kills));
    this.setStat('deaths', String(snap.deaths));

    this.pointCount.textContent = String(snap.points);
    this.pointBanner.classList.toggle('none', snap.points <= 0);

    const values: Record<StatKey, number> = { str: snap.str, agi: snap.agi, vit: snap.vit };
    for (const stat of ATTR_ORDER) {
      const value = this.attrValues.get(stat);
      if (value) value.textContent = String(values[stat]);
      const button = this.attrButtons.get(stat);
      if (button) button.disabled = snap.points <= 0;
    }
  }

  /* -------------------------------------------------------------------- shop */

  get shopOpen(): boolean {
    return !this.shopPanel.classList.contains('hidden');
  }

  openShop(vendor: NpcInfo): void {
    this.ensureShop();
    this.shopRole = vendor.role;
    this.shopName.textContent = vendor.name;
    // Marrow sells potions and scrolls; the two smiths sell only gear.
    this.shopList.classList.toggle('hidden', vendor.role !== 'trader');
    this.shopPanel.classList.remove('hidden');
    this.closeCharPanel();
    this.closeQuestPanel();
    this.gearPanel.classList.add('hidden');
    if (this.lastSnapshot) this.updateShop(this.lastSnapshot);
  }

  closeShop(): void {
    this.shopPanel.classList.add('hidden');
    this.hideTooltip();
  }

  toggleShop(trader: NpcInfo): void {
    if (this.shopOpen) this.closeShop();
    else this.openShop(trader);
  }

  setTradePrompt(npc: NpcInfo | null): void {
    if (!npc) {
      this.tradePrompt.classList.add('hidden');
      return;
    }
    const phrase =
      npc.role === 'questgiver'
        ? 'to speak with'
        : npc.role === 'monument'
          ? 'to read'
          : 'to trade with';
    this.tradePrompt.textContent = '';
    const kbd = document.createElement('kbd');
    kbd.textContent = 'E';
    this.tradePrompt.append(document.createTextNode('Press '), kbd, document.createTextNode(` ${phrase} ${npc.name}`));
    this.tradePrompt.classList.remove('hidden');
  }

  private ensureShop(): void {
    if (this.shopBuilt) return;
    this.shopBuilt = true;
    this.shopList.innerHTML = '';
    this.shopBuyButtons.clear();

    for (const id of SHOP_ORDER) {
      const def = ITEMS[id];
      const row = document.createElement('div');
      row.className = 'shop-row';

      const icon = document.createElement('canvas');
      icon.className = 'shop-icon';
      drawIconCanvas(icon, id, 34);

      const name = document.createElement('div');
      name.className = 'shop-name';
      name.textContent = def.name;

      const desc = document.createElement('div');
      desc.className = 'shop-desc';
      desc.textContent = def.description;

      const actions = document.createElement('div');
      actions.className = 'shop-buy';

      const buyOne = document.createElement('button');
      buyOne.type = 'button';
      buyOne.textContent = `${def.price} c`;
      buyOne.addEventListener('click', () => this.handlers.onBuy(id, 1));

      const buyFive = document.createElement('button');
      buyFive.type = 'button';
      buyFive.textContent = `x5 ${def.price * 5} c`;
      buyFive.addEventListener('click', () => this.handlers.onBuy(id, 5));

      actions.append(buyOne, buyFive);
      row.append(icon, name, desc, actions);
      this.shopList.appendChild(row);
      this.shopBuyButtons.set(`${id}:1`, buyOne);
      this.shopBuyButtons.set(`${id}:5`, buyFive);
    }
  }

  private updateShop(snap: PlayerSnapshot): void {
    this.ensureShop();
    this.shopCoins.textContent = String(snap.coins);
    for (const id of SHOP_ORDER) {
      const def = ITEMS[id];
      const price = itemPrice(def, snap.level);
      const bagFull = (snap.bag[id] ?? 0) >= 99;
      const one = this.shopBuyButtons.get(`${id}:1`);
      const five = this.shopBuyButtons.get(`${id}:5`);
      if (one) {
        one.textContent = `${price} c`;
        one.disabled = bagFull || snap.coins < price;
      }
      if (five) {
        five.textContent = `x5 ${(price * 5).toLocaleString()} c`;
        five.disabled = bagFull || snap.coins < price * 5;
      }
    }
    this.renderShopGear();
  }

  /** The current vendor's gear stock, rebuilt only when it or the wallet changes. */
  private renderShopGear(): void {
    const stock =
      this.shopRole === 'weaponeer'
        ? this.shopWeapons
        : this.shopRole === 'armourer'
          ? this.shopArmour
          : [];
    const coins = this.lastSnapshot?.coins ?? 0;
    const key = JSON.stringify({ role: this.shopRole, coins, uids: stock.map((item) => item.uid) });
    if (key === this.shopGearKey) return;
    this.shopGearKey = key;

    this.shopGear.innerHTML = '';
    if (stock.length === 0) return;

    const heading = document.createElement('h4');
    heading.className = 'shop-section';
    heading.textContent = this.shopRole === 'weaponeer' ? 'Weapons for sale' : 'Armour for sale';
    this.shopGear.appendChild(heading);

    for (const item of stock) {
      const row = document.createElement('div');
      row.className = 'shop-row shop-gear-row';

      const info = document.createElement('div');
      info.className = 'shop-item';
      info.addEventListener('mouseenter', () => this.showTooltip(item, this.equipment[item.slot] ?? null, info));
      info.addEventListener('mouseleave', () => this.hideTooltip());
      const name = document.createElement('div');
      name.className = 'shop-name';
      name.textContent = item.name;
      name.style.color = RARITIES[item.rarity].color;
      const desc = document.createElement('div');
      desc.className = 'shop-desc';
      desc.textContent = `${SLOT_LABELS[item.slot]} · ilvl ${item.ilvl}`;
      info.append(name, desc);

      const price = gearPrice(item);
      const buy = document.createElement('button');
      buy.type = 'button';
      buy.className = 'shop-buy-btn';
      buy.textContent = `${price} c`;
      buy.disabled = coins < price;
      buy.addEventListener('click', () => this.handlers.onBuyGear(item.uid));

      row.append(info, buy);
      this.shopGear.appendChild(row);
    }
  }

  /* ------------------------------------------------------------------- quests */

  get questPanelOpen(): boolean {
    return !this.questPanel.classList.contains('hidden');
  }

  openQuestPanel(giver: NpcInfo): void {
    this.questGiverName.textContent = giver.name;
    this.closeShop();
    this.closeCharPanel();
    this.questPanel.classList.remove('hidden');
    // Force a rebuild so the list reflects the current level.
    this.offersKey = '\u0000';
    if (this.lastSnapshot) this.updateQuests(this.lastSnapshot);
  }

  closeQuestPanel(): void {
    this.questPanel.classList.add('hidden');
  }

  toggleQuestPanel(giver: NpcInfo): void {
    if (this.questPanelOpen) this.closeQuestPanel();
    else this.openQuestPanel(giver);
  }

  private updateQuests(snap: PlayerSnapshot): void {
    const active = snap.quests ?? [];
    this.updateQuestTracker(active);
    if (this.questPanelOpen) this.renderQuestPanel(snap, active);
  }

  /**
   * Rows are built once and then patched. Rebuilding them every frame would
   * destroy the buttons between mousedown and mouseup, and the clicks would
   * never land.
   */
  private renderQuestPanel(snap: PlayerSnapshot, active: QuestSnapshot[]): void {
    const activeIds = new Set(active.map((q) => q.id));
    const full = active.length >= MAX_ACTIVE_QUESTS;
    this.questCapacity.textContent = `${active.length} / ${MAX_ACTIVE_QUESTS}`;

    // --- offers
    const key = `${snap.level}|${[...activeIds].sort().join(',')}`;
    if (key !== this.offersKey) {
      this.offersKey = key;
      this.questOffers.innerHTML = '';
      this.offerButtons.clear();

      const available = offersFor(snap.level, this.landmarks).filter((o) => !activeIds.has(o.id));
      if (available.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'quest-empty';
        empty.textContent =
          active.length >= MAX_ACTIVE_QUESTS
            ? 'You are carrying all you can. Finish something first.'
            : 'Nothing suitable for your level right now.';
        this.questOffers.appendChild(empty);
      }

      for (const offer of available) {
        const row = document.createElement('div');
        row.className = 'quest-row';

        const title = document.createElement('div');
        title.className = 'quest-title';
        title.textContent = offer.name;

        const blurb = document.createElement('div');
        blurb.className = 'quest-blurb';
        blurb.textContent = questBlurb(offer);

        const reward = document.createElement('div');
        reward.className = 'quest-reward';
        reward.textContent = `${offer.xpReward} xp · ${offer.coinReward} coins`;

        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = 'Accept';
        button.addEventListener('click', () => this.handlers.onAcceptQuest(offer.id));

        row.append(title, blurb, reward, button);
        this.questOffers.appendChild(row);
        this.offerButtons.set(offer.id, button);
      }
    }
    for (const button of this.offerButtons.values()) button.disabled = full;

    // --- in progress
    const seen = new Set<string>();
    for (const quest of active) {
      seen.add(quest.id);
      let row = this.activeQuestRows.get(quest.id);
      if (!row) {
        const root = document.createElement('div');
        root.className = 'quest-row done';

        const title = document.createElement('div');
        title.className = 'quest-title';
        title.textContent = quest.name;

        const bar = document.createElement('div');
        bar.className = 'quest-progress';
        const fill = document.createElement('i');
        bar.appendChild(fill);

        const label = document.createElement('div');
        label.className = 'quest-reward';

        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = 'Abandon';
        button.addEventListener('click', () => this.handlers.onAbandonQuest(quest.id));

        root.append(title, bar, label, button);
        this.questActive.appendChild(root);
        row = { root, label, bar: fill, button };
        this.activeQuestRows.set(quest.id, row);
      }
      const ratio = quest.count > 0 ? quest.progress / quest.count : 0;
      row.label.textContent = `${quest.progress} / ${quest.count} · ${quest.xpReward} xp · ${quest.coinReward} coins`;
      row.bar.style.width = `${Math.min(100, ratio * 100)}%`;
    }
    for (const [id, row] of [...this.activeQuestRows]) {
      if (seen.has(id)) continue;
      row.root.remove();
      this.activeQuestRows.delete(id);
    }
  }

  private updateQuestTracker(active: QuestSnapshot[]): void {
    const key = active.map((q) => `${q.id}:${q.progress}`).join('|');
    if (key === this.trackerKey) return;
    this.trackerKey = key;

    this.questTracker.classList.toggle('hidden', active.length === 0);
    this.trackerList.innerHTML = '';
    for (const quest of active) {
      const row = document.createElement('div');
      row.className = 'tracker-row';

      const head = document.createElement('div');
      head.className = 'tracker-head';
      const name = document.createElement('b');
      name.textContent = quest.targetName ? `${quest.name} \u00b7 ${quest.targetName}` : quest.name;
      const count = document.createElement('span');
      count.textContent = quest.targetName ? 'boss' : `${quest.progress}/${quest.count}`;
      head.append(name, count);

      const bar = document.createElement('div');
      bar.className = 'quest-progress';
      const fill = document.createElement('i');
      fill.style.width = `${Math.min(100, (quest.progress / quest.count) * 100)}%`;
      bar.appendChild(fill);

      row.append(head, bar);
      this.trackerList.appendChild(row);
    }
  }

  /* ------------------------------------------------------------------ social */

  get socialPanelOpen(): boolean {
    return !this.socialPanel.classList.contains('hidden');
  }

  setSocial(state: SocialState): void {
    const previous = this.social;
    this.social = state;
    if (this.socialPanelOpen) this.renderSocial();
    // A new invitation is worth interrupting for.
    const before = new Set((previous?.invites ?? []).map((i) => i.name));
    for (const invite of state.invites) {
      if (before.has(invite.name)) continue;
      this.addToast(`${invite.name} invites you to a party - press P.`, 'level');
    }
  }

  get partyLeaderName(): string | null {
    return this.social?.party?.leader ?? null;
  }

  toggleSocialPanel(): void {
    if (this.socialPanelOpen) {
      this.closeSocialPanel();
      return;
    }
    this.closeShop();
    this.closeQuestPanel();
    this.closeCharPanel();
    this.socialPanel.classList.remove('hidden');
    this.renderSocial();
  }

  closeSocialPanel(): void {
    this.socialPanel.classList.add('hidden');
  }

  /** A compact portrait chip used by every row. */
  private socialDot(cls: ClassId | null, name: string): HTMLElement {
    const def = CLASSES[cls ?? 'warrior'] ?? CLASSES.warrior;
    const dot = document.createElement('span');
    dot.className = 'social-dot';
    dot.style.background = cls ? def.color : '#3a4351';
    dot.style.color = cls ? def.accent : '#8b9ab3';
    dot.textContent = name.charAt(0).toUpperCase();
    return dot;
  }

  private socialRow(
    cls: ClassId | null,
    name: string,
    meta: string,
    actions: HTMLElement[],
    hp?: { hp: number; maxHp: number },
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'social-row';

    const label = document.createElement('span');
    label.className = 'social-name';
    label.append(document.createTextNode(name));
    const small = document.createElement('small');
    small.textContent = meta;
    label.append(small);

    const actionBox = document.createElement('span');
    actionBox.className = 'social-actions';
    actionBox.append(...actions);

    row.append(this.socialDot(cls, name), label, actionBox);

    if (hp) {
      const bar = document.createElement('div');
      bar.className = 'party-hp';
      const fill = document.createElement('i');
      fill.style.width = `${Math.max(0, Math.min(100, (hp.hp / Math.max(1, hp.maxHp)) * 100))}%`;
      bar.appendChild(fill);
      row.appendChild(bar);
    }
    return row;
  }

  private button(label: string, className: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  private emptyNote(host: HTMLElement, text: string): void {
    const note = document.createElement('div');
    note.className = 'social-empty';
    note.textContent = text;
    host.appendChild(note);
  }

  private renderSocial(): void {
    const state = this.social;
    const leaderName = this.social?.party?.leader ?? null;
    const amLeader = leaderName !== null && leaderName === this.lastSnapshot?.name;

    // --- invitations
    this.socialInvites.innerHTML = '';
    for (const invite of state?.invites ?? []) {
      const banner = document.createElement('div');
      banner.className = 'invite-banner';
      const text = document.createElement('p');
      text.textContent = `${invite.name} (level ${invite.level}) invites you to a party.`;
      const actions = document.createElement('div');
      actions.className = 'social-actions';
      actions.append(
        this.button('Accept', 'accept', () => this.handlers.onPartyAccept(invite.name)),
        this.button('Decline', 'danger', () => this.handlers.onPartyDecline(invite.name)),
      );
      banner.append(text, actions);
      this.socialInvites.appendChild(banner);
    }

    // --- party
    this.partyList.innerHTML = '';
    const members = state?.party?.members ?? [];
    this.partyCount.textContent = members.length ? String(members.length) : '';
    this.partyLeave.classList.toggle('hidden', members.length === 0);
    if (members.length === 0) {
      this.emptyNote(this.partyList, 'Not in a party. Invite someone from the list below.');
    } else {
      for (const member of members) {
        const actions: HTMLElement[] = [];
        if (amLeader && !member.leader) {
          actions.push(this.button('Remove', 'danger', () => this.handlers.onPartyKick(member.name)));
        }
        const name = member.leader ? `${member.name} \u2605` : member.name;
        this.partyList.appendChild(
          this.socialRow(
            member.cls,
            name,
            `Level ${member.level} ${CLASSES[member.cls].name}`,
            actions,
            { hp: member.hp, maxHp: member.maxHp },
          ),
        );
      }
    }

    // --- friends
    this.friendList.innerHTML = '';
    const friends = state?.friends ?? [];
    const onlineFriends = friends.filter((f) => f.online).length;
    this.friendCount.textContent = friends.length ? `${onlineFriends}/${friends.length} on` : '';
    if (friends.length === 0) {
      this.emptyNote(this.friendList, 'No friends yet. Add someone by name below.');
    }
    for (const friend of friends) {
      const actions: HTMLElement[] = [];
      if (friend.online) {
        actions.push(this.button('Invite', '', () => this.handlers.onPartyInvite(friend.name)));
      }
      actions.push(this.button('Remove', 'danger', () => this.handlers.onFriendRemove(friend.name)));
      const meta = friend.online
        ? `Online \u00b7 level ${friend.level} ${CLASSES[friend.cls ?? 'warrior'].name}`
        : friend.lastSeen
          ? `Offline \u00b7 seen ${relativeTime(friend.lastSeen)}`
          : 'Offline';
      this.friendList.appendChild(this.socialRow(friend.cls, friend.name, meta, actions));
    }

    // --- everyone online
    this.onlineList.innerHTML = '';
    const online = state?.online ?? [];
    this.onlineCount.textContent = online.length ? String(online.length) : '';
    if (online.length === 0) {
      this.emptyNote(this.onlineList, 'Nobody else is online.');
    }
    for (const player of online) {
      const isFriend = friends.some((f) => f.name.toLowerCase() === player.name.toLowerCase());
      const actions: HTMLElement[] = [
        this.button('Invite', '', () => this.handlers.onPartyInvite(player.name)),
      ];
      if (!isFriend) {
        actions.push(this.button('+ Friend', '', () => this.handlers.onFriendAdd(player.name)));
      }
      this.onlineList.appendChild(
        this.socialRow(player.cls, player.name, `Level ${player.level} ${CLASSES[player.cls].name}`, actions),
      );
    }
  }

  /* -------------------------------------------------------------------- pause */

  get pauseOpen(): boolean {
    return !this.pauseMenu.classList.contains('hidden');
  }

  togglePause(): void {
    if (this.pauseOpen) this.closePause();
    else this.openPause();
  }

  openPause(): void {
    // The pause menu is the top of the stack; nothing else should be up.
    this.closeChat();
    this.closeShop();
    this.closeQuestPanel();
    this.closeSocialPanel();
    this.closeCharPanel();
    this.pauseMenu.classList.remove('hidden');
  }

  closePause(): void {
    this.pauseMenu.classList.add('hidden');
  }

  /* ------------------------------------------------------------------- events */

  private bindEvents(): void {
    el('join-form').addEventListener('submit', (event) => {
      event.preventDefault();
      const name = this.nameInput.value.trim();
      if (name.length < 3) {
        this.setError('Pick a name that is at least 3 characters long.');
        return;
      }
      this.clearError();
      this.setPlayEnabled(false);
      this.handlers.onPlay(name, this.selectedClass);
    });

    this.chatInput.parentElement?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submitChat();
    });

    el('char-close').addEventListener('click', () => this.closeCharPanel());
    el('gear-close').addEventListener('click', () => this.closeGearPanel());

    this.confirmCancel.addEventListener('click', () => this.closeConfirm());
    this.confirmOk.addEventListener('click', () => {
      const handler = this.confirmHandler;
      this.closeConfirm();
      handler?.();
    });
    this.confirmPanel.addEventListener('click', (event) => {
      if (event.target === this.confirmPanel) this.closeConfirm();
    });
    // Escape closes the dialog without confirming, ahead of any other handler.
    window.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.confirmOpen) {
          event.stopPropagation();
          this.closeConfirm();
        }
      },
      true,
    );

    this.feedbackInput.addEventListener('input', () => {
      this.feedbackCount.textContent = `${this.feedbackInput.value.length} / ${FEEDBACK_MAX}`;
    });
    this.feedbackForm.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.sendFeedback();
    });
    el('shop-close').addEventListener('click', () => this.closeShop());
    el('quest-close').addEventListener('click', () => this.closeQuestPanel());
    el('social-close').addEventListener('click', () => this.closeSocialPanel());
    this.partyLeave.addEventListener('click', () => this.handlers.onPartyLeave());

    this.friendForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const name = this.friendInput.value.trim();
      if (!name) return;
      this.handlers.onFriendAdd(name);
      this.friendInput.value = '';
    });

    el('new-char-btn').addEventListener('click', () => {
      this.creatorVisible = true;
      this.clearError();
      this.updateLoginMode();
      this.nameInput.focus();
    });

    el('back-to-roster').addEventListener('click', () => {
      this.creatorVisible = false;
      this.clearError();
      this.updateLoginMode();
    });

    el('pause-resume').addEventListener('click', () => this.handlers.onResume());
    el('pause-logout').addEventListener('click', () => this.handlers.onLogout());

    // Clicking the game world should take focus away from any open panel.
    el('game').addEventListener('mousedown', () => {
      if (this.chatOpen) this.closeChat();
    });
  }
}
